import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './index.module.scss';
import SiteTopNav from '@/components/SiteTopNav';
import img_hero from '@/assets/images/pages-gallery.png';
import img_living from '@/assets/images/living_img.jpeg';
import img_dining from '@/assets/images/kitchen_img.jpeg';
import img_bedroom from '@/assets/images/bedroom_img.jpeg';
import img_hallway from '@/assets/images/hallway_img.jpeg';
import img_office from '@/assets/images/office_img.jpeg';
import img_kitchen from '@/assets/images/kitchen_img.jpeg';
import img_entry from '@/assets/images/entry_img.jpeg';
import img_lowerlevel from '@/assets/images/lower_img.jpeg';
import img_bathroom from '@/assets/images/bathroom_img.jpeg';
import img_continuity from '@/assets/images/pages-products-collections-collections-hero.png';

const META_DESCRIPTION =
  'Learn where engineered hardwood works best room by room, including living rooms, kitchens, entryways, basements, bathrooms, and other moisture-sensitive spaces.';

const RoomSelection = () => {
  useEffect(() => {
    const existing = document.querySelector('meta[name="description"]');
    const previousContent = existing ? existing.getAttribute('content') : null;
    const metaEl = existing || document.createElement('meta');
    if (!existing) {
      metaEl.setAttribute('name', 'description');
      document.head.appendChild(metaEl);
    }
    metaEl.setAttribute('content', META_DESCRIPTION);

    return () => {
      if (previousContent === null) {
        metaEl.remove();
      } else {
        metaEl.setAttribute('content', previousContent);
      }
    };
  }, []);

  return (
    <div className={styles['room-page']}>
      <div className={styles.page}>
        <header className={styles['article-header']}>
          <SiteTopNav variant="light" />
        </header>

        <main className={styles['article-main']}>
          <Link className={styles['article-back']} to="/academy/hardwood-basics">
            ← Back to Flooring 101
          </Link>

          <p className={styles['article-eyebrow']} data-motion="copy">
            Sakura Floors Academy
          </p>

          <h1 className={styles['article-title']} data-motion="hero">
            Choosing Hardwood Flooring
            <br />
            Room by Room
          </h1>

          <p className={styles['article-subtitle']} data-motion="copy">
            Where Engineered Hardwood Works Best — and Where to Use Extra Care
          </p>

          <p className={styles['article-intro']} data-motion="copy">
            Engineered hardwood performs beautifully across most of the home, but not every room
            places the same demands on a floor. Natural light, foot traffic, and moisture exposure
            all vary from space to space, and a floor that thrives in a sunlit living room may need
            extra care in a kitchen or basement.
          </p>

          <p className={styles['article-intro']} data-motion="copy">
            Engineered hardwood is more dimensionally stable than solid hardwood, but it is still
            real wood. Understanding how each room behaves — its subfloor, its installation method,
            and how it will be used day to day — helps you choose flooring with confidence and keep
            it beautiful for decades.
          </p>

          {/* <div className={styles['hero-media']} data-motion="section">
            <img
              className={styles['hero-media__image']}
              alt="An open-concept living space with engineered hardwood flooring flowing between rooms"
              referrerPolicy="no-referrer"
              src={img_hero}
            />
          </div> */}

          {/* Living Rooms & Great Rooms */}
          <section
            className={styles['room-section']}
            data-motion="section"
            aria-label="Living Rooms & Great Rooms"
          >
            <div className={styles['room-section__media']}>
              <img
                className={styles['room-section__image']}
                alt="A sunlit living room with a stone fireplace and wide-plank hardwood flooring"
                referrerPolicy="no-referrer"
                src={img_living}
              />
            </div>
            <div className={styles['room-section__content']}>
              <p className={styles['room-section__status']} data-tone="excellent">
                Excellent Choice
              </p>
              <h2 className={styles['room-section__title']}>Living Rooms &amp; Great Rooms</h2>
              <p className={styles['room-section__text']}>
                Living rooms and great rooms are where engineered hardwood is most at home. Moderate
                foot traffic, consistent indoor humidity, and generous natural light make these
                spaces ideal for showcasing the grain, tone, and warmth of a premium wear layer.
              </p>
              <p className={styles['room-section__text']}>
                In open-concept homes, a great room often sets the visual tone for the floor plan
                that follows. Wide, long-format planks emphasize sightlines and make the space feel
                larger, while a consistent finish sheen keeps the room feeling cohesive as light
                shifts throughout the day.
              </p>
            </div>
          </section>

          {/* Dining Rooms */}
          <section
            className={`${styles['room-section']} ${styles['room-section--reverse']}`}
            data-motion="section"
            aria-label="Dining Rooms"
          >
            <div className={styles['room-section__media']}>
              <img
                className={styles['room-section__image']}
                alt="A bright dining space with hardwood flooring opening onto a garden view"
                referrerPolicy="no-referrer"
                src={img_dining}
              />
            </div>
            <div className={styles['room-section__content']}>
              <p className={styles['room-section__status']} data-tone="excellent">
                Excellent Choice
              </p>
              <h2 className={styles['room-section__title']}>Dining Rooms</h2>
              <p className={styles['room-section__text']}>
                Dining rooms see occasional spills but relatively light, seated traffic, which makes
                them another strong fit for engineered hardwood. A quality finish resists everyday
                marks from chairs and table legs, and protective floor pads add an extra margin of
                confidence beneath frequently moved seating.
              </p>
              <p className={styles['room-section__text']}>
                Because dining areas are often visually connected to kitchens or great rooms,
                keeping plank width, direction, and finish consistent between them helps the floor
                read as one continuous surface rather than a patchwork of separate rooms.
              </p>
            </div>
          </section>

          {/* Bedrooms */}
          <section className={styles['room-section']} data-motion="section" aria-label="Bedrooms">
            <div className={styles['room-section__media']}>
              <img
                className={styles['room-section__image']}
                alt="A calm, minimally furnished bedroom with soft light on hardwood flooring"
                referrerPolicy="no-referrer"
                src={img_bedroom}
              />
            </div>
            <div className={styles['room-section__content']}>
              <p className={styles['room-section__status']} data-tone="excellent">
                Excellent Choice
              </p>
              <h2 className={styles['room-section__title']}>Bedrooms</h2>
              <p className={styles['room-section__text']}>
                Bedrooms combine low foot traffic with minimal moisture exposure, making them one of
                the gentlest environments in the home for hardwood flooring. Many homeowners choose
                a softer wood species or a matte finish here, prioritizing warmth underfoot and a
                quiet, understated grain pattern over high-traffic durability.
              </p>
              <p className={styles['room-section__text']}>
                An area rug at the bedside adds comfort without hiding the floor, and because
                bedrooms are typically climate-controlled year-round, engineered hardwood remains
                dimensionally stable with very little seasonal movement.
              </p>
            </div>
          </section>

          {/* Hallways & Connecting Spaces */}
          <section
            className={`${styles['room-section']} ${styles['room-section--reverse']}`}
            data-motion="section"
            aria-label="Hallways & Connecting Spaces"
          >
            <div className={styles['room-section__media']}>
              <img
                className={styles['room-section__image']}
                alt="A sunlit staircase and hallway with continuous hardwood flooring"
                referrerPolicy="no-referrer"
                src={img_hallway}
              />
            </div>
            <div className={styles['room-section__content']}>
              <p className={styles['room-section__status']} data-tone="excellent">
                Excellent Choice
              </p>
              <h2 className={styles['room-section__title']}>Hallways &amp; Connecting Spaces</h2>
              <p className={styles['room-section__text']}>
                Hallways carry more concentrated foot traffic than almost any other space, but they
                see very little moisture, which keeps engineered hardwood well suited to the job. A
                durable finish and a slightly harder wood species help these high-traffic corridors
                hold up over years of daily passage.
              </p>
              <p className={styles['room-section__text']}>
                Because hallways physically connect one room to the next, they are also where
                continuity matters most. Carrying the same plank width and finish through a hallway
                helps a home feel unified rather than divided into disconnected rooms.
              </p>
            </div>
          </section>

          {/* Home Offices & Libraries */}
          <section
            className={styles['room-section']}
            data-motion="section"
            aria-label="Home Offices & Libraries"
          >
            <div className={styles['room-section__media']}>
              <img
                className={styles['room-section__image']}
                alt="A refined home office and library with built-in shelving and hardwood flooring"
                referrerPolicy="no-referrer"
                src={img_office}
              />
            </div>
            <div className={styles['room-section__content']}>
              <p className={styles['room-section__status']} data-tone="excellent">
                Excellent Choice
              </p>
              <h2 className={styles['room-section__title']}>Home Offices &amp; Libraries</h2>
              <p className={styles['room-section__text']}>
                Home offices and libraries share many of the same favorable conditions as living
                rooms: steady humidity, light foot traffic, and few sources of moisture. These are
                also rooms where furniture — desks, bookcases, rolling chairs — tends to stay in
                place for years, so a felt-padded chair mat is worth adding beneath any casters.
              </p>
              <p className={styles['room-section__text']}>
                Rich, deeper tones and tighter grain patterns often suit the more formal character
                of a study, though the right choice ultimately comes down to how much natural light
                the room receives and the mood you want it to hold.
              </p>
            </div>
          </section>

          {/* Kitchens */}
          <section
            className={`${styles['room-section']} ${styles['room-section--reverse']}`}
            data-motion="section"
            aria-label="Kitchens"
          >
            <div className={styles['room-section__media']}>
              <img
                className={styles['room-section__image']}
                alt="A modern kitchen with an island and engineered hardwood flooring"
                referrerPolicy="no-referrer"
                src={img_kitchen}
              />
            </div>
            <div className={styles['room-section__content']}>
              <p className={styles['room-section__status']} data-tone="care">
                Suitable With Additional Care
              </p>
              <h2 className={styles['room-section__title']}>Kitchens</h2>
              <p className={styles['room-section__text']}>
                Engineered hardwood can work well in kitchens, and many homeowners love the warmth
                it brings to a space that's usually filled with hard, cold surfaces. That said,
                kitchens see more moisture exposure than almost any other living space — from
                cooking, dish washing, and appliance use — so spills and standing water need prompt
                attention.
              </p>
              <ul className={styles['room-section__list']}>
                <li>Wipe spills as soon as they happen, especially near the sink and dishwasher</li>
                <li>Use a floor mat at the sink and in front of the stove for daily protection</li>
                <li>Choose a durable finish suited to a busy, high-use room</li>
              </ul>
            </div>
          </section>

          {/* Entryways & Foyers */}
          <section
            className={styles['room-section']}
            data-motion="section"
            aria-label="Entryways & Foyers"
          >
            <div className={styles['room-section__media']}>
              <img
                className={styles['room-section__image']}
                alt="A welcoming entryway with a console table and hardwood flooring"
                referrerPolicy="no-referrer"
                src={img_entry}
              />
            </div>
            <div className={styles['room-section__content']}>
              <p className={styles['room-section__status']} data-tone="care">
                Suitable With Additional Care
              </p>
              <h2 className={styles['room-section__title']}>Entryways &amp; Foyers</h2>
              <p className={styles['room-section__text']}>
                Entryways are the first impression of a home, but they also absorb the most exposure
                to the outdoors — rain, snow melt, tracked-in grit, and seasonal humidity swings all
                pass through this one small space.
              </p>
              <p className={styles['room-section__text']}>
                A quality entry mat placed just inside the door captures moisture and debris before
                it reaches the floor, and a durable finish helps the entry hold up to fine grit,
                which can otherwise dull a floor's surface over time.
              </p>
            </div>
          </section>

          {/* Lower Levels & Concrete Slabs */}
          <section
            className={`${styles['room-section']} ${styles['room-section--reverse']}`}
            data-motion="section"
            aria-label="Lower Levels & Concrete Slabs"
          >
            <div className={styles['room-section__media']}>
              <img
                className={styles['room-section__image']}
                alt="Close-up of engineered hardwood planks being installed over a subfloor"
                referrerPolicy="no-referrer"
                src={img_lowerlevel}
              />
            </div>
            <div className={styles['room-section__content']}>
              <p className={styles['room-section__status']} data-tone="evaluate">
                Evaluate Conditions First
              </p>
              <h2 className={styles['room-section__title']}>Lower Levels &amp; Concrete Slabs</h2>
              <p className={styles['room-section__text']}>
                Engineered hardwood's layered construction makes it far better suited to below-grade
                spaces than solid hardwood, but a lower level still deserves a closer look before
                installation begins. Concrete slabs can hold residual moisture long after a home is
                built, and below-grade humidity tends to run higher and less predictable than the
                floors above.
              </p>
              <p className={styles['room-section__text']}>
                A qualified installer should perform moisture testing on the slab, confirm the
                subfloor is level and adequately dry, and recommend an installation method —
                typically glue-down or a suitable floating system — matched to those conditions.
                Ongoing humidity control, such as a dehumidifier in damp climates, helps protect the
                investment long after installation.
              </p>
            </div>
          </section>

          {/* Bathrooms & Other Wet Areas */}
          <section
            className={styles['room-section']}
            data-motion="section"
            aria-label="Bathrooms & Other Wet Areas"
          >
            <div className={styles['room-section__media']}>
              <img
                className={styles['room-section__image']}
                alt="Bare feet stepping onto warm hardwood flooring"
                referrerPolicy="no-referrer"
                src={img_bathroom}
              />
            </div>
            <div className={styles['room-section__content']}>
              <p className={styles['room-section__status']} data-tone="avoid">
                Generally Not Recommended
              </p>
              <h2 className={styles['room-section__title']}>Bathrooms &amp; Other Wet Areas</h2>
              <p className={styles['room-section__text']}>
                Sakura Floors engineered hardwood is not waterproof, and bathrooms are generally not
                recommended for hardwood flooring. Frequent standing water around tubs, showers, and
                sinks — along with the humidity that builds up during everyday use — creates
                conditions that real wood, even dimensionally stable engineered wood, is not
                designed to withstand over the long term.
              </p>
              <p className={styles['room-section__text']}>
                The same caution applies to laundry rooms and other spaces with a real risk of
                appliance leaks or standing water. For these rooms, a moisture-resistant surface is
                the more dependable choice, with hardwood reserved for the drier living spaces that
                surround them.
              </p>
            </div>
          </section>

          {/* Room-by-Room Comparison Guide */}
          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>At a Glance</p>
            <h2 className={styles['article-section__title']}>Room-by-Room Comparison Guide</h2>
            <p className={styles['article-section__text']}>
              A quick reference for how engineered hardwood performs across the rooms of a typical
              home.
            </p>

            <div className={styles['article-table-wrap']}>
              <table className={styles['article-table']}>
                <thead>
                  <tr>
                    <th scope="col">Room</th>
                    <th scope="col">Suitability</th>
                    <th scope="col">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Living Room</td>
                    <td>Excellent</td>
                    <td>Ideal light and traffic conditions for hardwood</td>
                  </tr>
                  <tr>
                    <td>Great Room</td>
                    <td>Excellent</td>
                    <td>Wide planks emphasize open, connected sightlines</td>
                  </tr>
                  <tr>
                    <td>Dining Room</td>
                    <td>Excellent</td>
                    <td>Low moisture; use pads under frequently moved chairs</td>
                  </tr>
                  <tr>
                    <td>Bedroom</td>
                    <td>Excellent</td>
                    <td>Low traffic and low moisture; pairs well with area rugs</td>
                  </tr>
                  <tr>
                    <td>Hallway</td>
                    <td>Excellent</td>
                    <td>High traffic but low moisture; choose a durable finish</td>
                  </tr>
                  <tr>
                    <td>Home Office</td>
                    <td>Excellent</td>
                    <td>Stable and quiet; pad any rolling chair casters</td>
                  </tr>
                  <tr>
                    <td>Kitchen</td>
                    <td>Suitable with care</td>
                    <td>Wipe spills promptly; use mats at the sink and stove</td>
                  </tr>
                  <tr>
                    <td>Entry / Foyer</td>
                    <td>Suitable with care</td>
                    <td>Use an entry mat to capture grit and moisture</td>
                  </tr>
                  <tr>
                    <td>Basement / Lower Level</td>
                    <td>Evaluate first</td>
                    <td>Requires moisture testing and a matched installation method</td>
                  </tr>
                  <tr>
                    <td>Bathroom</td>
                    <td>Generally not recommended</td>
                    <td>Frequent standing water and humidity are a poor match</td>
                  </tr>
                  <tr>
                    <td>Laundry Room</td>
                    <td>Generally not recommended</td>
                    <td>Appliance leaks and standing water create ongoing risk</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Think Beyond the Individual Room */}
          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>The Bigger Picture</p>
            <h2 className={styles['article-section__title']}>Think Beyond the Individual Room</h2>
            <p className={styles['article-section__text']}>
              A floor rarely lives in just one room. In open floor plans especially, the decisions
              that matter most are often the ones that carry a floor smoothly from space to space.
            </p>

            <div className={styles['considerations-grid']}>
              <div className={styles.consideration}>
                <h3 className={styles['consideration__title']}>Natural Light</h3>
                <p className={styles['consideration__text']}>
                  Sun exposure varies room to room and can gradually shift a wood tone over time.
                  Consistent light levels help a floor age evenly across connected spaces.
                </p>
              </div>
              <div className={styles.consideration}>
                <h3 className={styles['consideration__title']}>Foot Traffic</h3>
                <p className={styles['consideration__text']}>
                  High-traffic corridors between rooms benefit from a harder species and a more
                  durable finish than quieter spaces like bedrooms.
                </p>
              </div>
              <div className={styles.consideration}>
                <h3 className={styles['consideration__title']}>Plank Dimensions &amp; Grade</h3>
                <p className={styles['consideration__text']}>
                  Carrying the same plank width, length, and grade through adjoining rooms keeps the
                  visual rhythm of the floor consistent rather than fragmented.
                </p>
              </div>
              <div className={styles.consideration}>
                <h3 className={styles['consideration__title']}>Subfloor &amp; Installation</h3>
                <p className={styles['consideration__text']}>
                  Different rooms may sit on different subfloors. Confirming conditions room by room
                  ensures the right installation method is used throughout.
                </p>
              </div>
              <div className={styles.consideration}>
                <h3 className={styles['consideration__title']}>Maintenance Routine</h3>
                <p className={styles['consideration__text']}>
                  A single, consistent care routine — regular sweeping, prompt spill cleanup, and
                  the right cleaning products — protects hardwood across every room it touches.
                </p>
              </div>
            </div>
          </section>

          {/* The Sakura Floors Approach */}
          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Our Perspective</p>
            <h2 className={styles['article-section__title']}>The Sakura Floors Approach</h2>
            <p className={styles['article-section__text']}>
              At Sakura Floors, we believe the best flooring decisions come from understanding how a
              home is actually lived in — not from treating every room the same. Our engineered
              hardwood is built to bring genuine wood into as much of the home as conditions allow,
              while giving homeowners honest guidance about the rooms where extra care, or a
              different material altogether, makes more sense.
            </p>
            <p className={styles['article-section__text']}>
              If you're planning a full-home installation, our team can help you think through
              continuity between rooms, transitions at doorways, and the right species, grade, and
              plank dimensions for the way you live.
            </p>
            {/* <ul className={styles['article-links']}>
              <li>
                <Link
                  className={styles['article-links__link']}
                  to="/academy/buying-guide/engineered-vs-solid-hardwood"
                >
                  Engineered vs. Solid Hardwood Flooring
                </Link>
              </li>
              <li>
                <Link
                  className={styles['article-links__link']}
                  to="/academy/buying-guide/installation-considerations"
                >
                  Installation Considerations
                </Link>
              </li>
              <li>
                <Link className={styles['article-links__link']} to="/academy/floor-care">
                  Floor Care &amp; Maintenance Guide
                </Link>
              </li>
              <li>
                <Link className={styles['article-links__link']} to="/products">
                  Browse Sakura Floors Collections
                </Link>
              </li>
            </ul> */}
          </section>
        </main>
      </div>
    </div>
  );
};

export default RoomSelection;
